import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { guideUrl } from "@/lib/guides";
import { Panel } from "@/components/AppShell";
import { pension } from "@/lib/fers";
import { spouseOf } from "@/lib/household";
import { usd } from "@/lib/format";

const OPTIONS = [["none", "None"], ["partial", "Partial"], ["full", "Full"]];
const THIS_YEAR = new Date().getFullYear();

const sumCola = (amt, years, cola) => { let t = 0; for (let i = 0; i < years; i++) t += amt * Math.pow(1 + cola, i); return t; };

function compare(p) {
  const s = spouseOf(p);
  const youEnd = THIS_YEAR + (p.life_expectancy - p.age);
  const survYears = s.enabled ? Math.max(0, THIS_YEAR + (Number(s.life_expectancy) - Number(s.age)) - youEnd) : null;
  const none = pension({ ...p, survivor_benefit: "none" });
  const payYears = Math.max(0, Math.round(p.life_expectancy - none.startAge));
  const cola = none.colaPct / 100;
  return OPTIONS.map(([k, l]) => {
    const pen = pension({ ...p, survivor_benefit: k });
    const costYr = none.annual - pen.annual;
    const lifetimeCost = sumCola(costYr, payYears, cola);
    const survAnnual = pen.base * pen.survivorPct * Math.pow(1 + cola, payYears);
    const survLifetime = survYears == null ? null : sumCola(survAnnual, survYears, cola);
    return { k, l, pen, costYr, lifetimeCost, survAnnual, survLifetime, payback: survAnnual > 0 ? lifetimeCost / survAnnual : null };
  }).map((r) => ({ ...r, survYears, spouseName: s.enabled ? s.name || "Spouse" : null }));
}

function Line({ k, v, testid }) {
  return <div className="flex justify-between gap-3 text-sm border-b border-border/60 py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-mono-num text-right" data-testid={testid}>{v}</span></div>;
}

export function SurvivorCompare({ p, setP }) {
  const rows = compare(p);
  if (!rows[0].pen.eligible) return null;
  const { survYears, spouseName } = rows[0];
  return (
    <Panel title="Survivor benefit comparison" testid="panel-survivor-compare">
      <div className="grid sm:grid-cols-3 gap-4">
        {rows.map((r) => {
          const on = (p.survivor_benefit || "none") === r.k;
          return (
            <div key={r.k} className={`rounded-xl border p-4 transition-colors duration-200 ${on ? "border-emerald-500 bg-emerald-500/5" : "border-border"}`} data-testid={`survivor-option-${r.k}`}>
              <p className="font-display font-semibold">{r.l}{on && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-600">Current</span>}</p>
              <p className="font-mono-num text-2xl font-semibold mt-2" data-testid={`survivor-option-${r.k}-monthly`}>{usd(r.pen.annual / 12)}<span className="text-sm text-muted-foreground">/mo to you</span></p>
              <div className="mt-3">
                <Line k="Cost per month" v={usd(r.costYr / 12)} testid={`survivor-option-${r.k}-cost`} />
                <Line k={`Lifetime cost (to ${p.life_expectancy})`} v={usd(r.lifetimeCost)} testid={`survivor-option-${r.k}-lifetime-cost`} />
                <Line k="Survivor gets" v={`${usd(r.survAnnual / 12)}/mo`} testid={`survivor-option-${r.k}-survivor-monthly`} />
                {r.survLifetime != null && <Line k={`Survivor total (${survYears} yrs)`} v={usd(r.survLifetime)} testid={`survivor-option-${r.k}-survivor-total`} />}
                <Line k="Pays back after" v={r.payback == null ? "—" : `${r.payback.toFixed(1)} yrs`} testid={`survivor-option-${r.k}-payback`} />
              </div>
              <Button size="sm" variant={on ? "secondary" : "outline"} className="rounded-full mt-4 w-full" disabled={on} onClick={() => setP({ ...p, survivor_benefit: r.k })} data-testid={`survivor-option-${r.k}-use`}>{on ? "Selected" : "Use this"}</Button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-4">Survivor amounts are shown in the year you'd reach your life expectancy, after COLA. {spouseName ? `${spouseName} is projected to outlive you by ${survYears} years.` : "Turn on a spouse in the Income Plan to see the survivor's lifetime total."} Payback is how many years the survivor must collect to recover what the election cost you. Full and partial elections also let the survivor keep FEHB health coverage. <Link to={guideUrl("fehb")} className="underline text-cyan-700 dark:text-cyan-400" data-testid="survivor-compare-fehb-guide-link">Read about the FEHB 5-year rule</Link>.</p>
    </Panel>
  );
}
